import { Organization } from '@core/models/organization';
import { routePaths } from '@screens/routes';
import { Tooltip } from 'antd';
import classNames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useMatch, useNavigate, useParams } from 'react-router';
import { Amplitude } from '../../../amplitude';
import { Actions } from '../../../redux/actions';
import { disableAnomalyDetection, setSideMapEnabled } from '../../../redux/map/thunks';
import type { RootState } from '../../../redux/reducers';
import type { MenuRoute } from '../options';
import BetaTag from './BetaTag';
import styles from './_sideBarSectionItem.module.scss';

const { setMapViewMode } = Actions;

export const SideBarSectionItem: React.FC<{
  item: MenuRoute;
  organization?: Organization | null;
  workspaceId?: string;
}> = ({ item, organization, workspaceId }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { organizationId, propertyId } = useParams();
  const navigate = useNavigate();

  const beta_features_list = import.meta.env.VITE_APP_BR_BETA_FEATURES?.split(',') || [];

  const isBetaFeature =
    organization?.country === 'BR' &&
    beta_features_list.map((feature) => `sidebar.${feature}`).includes(item.label);

  const { sidebarCollapsed } = useSelector((state: RootState) => state.App);

  const isOnAiInsights = useMatch(routePaths.aiInsights);
  const isOnWorkspaceDashboard = useMatch(routePaths.workspaceDashboard);
  const isOnItemPath = useMatch({ path: item.path, end: item.isEndRoute });
  // the hall path also matches the dashboard nested under it
  const isHallShadowedByDashboard =
    item.path === routePaths.organizations && Boolean(isOnWorkspaceDashboard);
  const isActive =
    (Boolean(isOnItemPath) && !isHallShadowedByDashboard) ||
    (item.path === routePaths.view && Boolean(isOnAiInsights));

  const goToScreen = () => {
    // Navigation is guarded centrally by the unsaved-changes route blocker
    // (see useUnsavedChangesBlocker), so navigate directly here.
    item.navigate?.({ organizationId, propertyId, workspaceId }, navigate);
    dispatch(setSideMapEnabled(false));
    Amplitude.trackSidebarClick(item.amplitudeName);
    dispatch(setMapViewMode('map'));
    dispatch(disableAnomalyDetection());
  };

  return (
    <Tooltip
      title={
        isBetaFeature ? (
          <div className="inline-flex items-start p-0.5 gap-2">
            {t(item.label)}
            <BetaTag />
          </div>
        ) : (
          t(item.label)
        )
      }
      placement={'right'}
      overlayClassName={styles.tooltip}
    >
      <button
        id={item.id}
        type="button"
        onClick={goToScreen}
        className={classNames(styles.sectionItemWrapper, {
          [styles.sectionSelectedItemWrapper]: isActive,
        })}
        role="menuitem"
      >
        {item.icon}
        {!sidebarCollapsed && <span className={styles.sectionItemTitle}>{t(item.label)}</span>}
      </button>
    </Tooltip>
  );
};
